import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Email templates.
 *
 * Perechile vechi: `settings/easeedesk/email_templates_settings`,
 * `get_templates_list`, `create_template`, `create_template_here`,
 * `update_template`, `remove_template`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface EmailTemplateRow {
  /** Numarul din `it_easeedesk_email_templates` - cel pe care il tine si o notificare. */
  template_id: number
  template_name: string
  template_subject: string
  template_body: string
  updated: string
  /** Numai la sabloanele emailului catre furnizor: cel care se deschide intai. */
  is_default?: boolean
  /** Numai la sabloanele emailului catre furnizor: furnizorii externi care il folosesc. */
  providers?: Array<{ id: number; name: string }>
}

export interface EmailTemplatesList {
  templates: EmailTemplateRow[]
}

/** Un simbol care se poate pune in cuprins: variabila comuna sau camp propriu. */
export interface TemplateOption {
  token: string
  label: string
}

export interface EmailTemplatesShell {
  title: string
  moduleUrl: string
  options: TemplateOption[]
}

export function emailTemplatesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'email_templates'],
    queryFn: () => apiGet<EmailTemplatesShell>('/settings/easeedesk/email_templates'),
  })
}

export function emailTemplatesListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'email_templates', 'list'],
    queryFn: () => apiGet<EmailTemplatesList>('/settings/easeedesk/email_templates/list'),
  })
}

export function createEmailTemplate(template_name: string, template_subject: string, template_body: string) {
  return apiPost<{ ok?: boolean; template?: { id: number; name: string } }>('/settings/easeedesk/email_templates', {
    template_name,
    template_subject,
    template_body,
  })
}

export function updateEmailTemplate(
  template: number,
  template_name: string,
  template_subject: string,
  template_body: string,
) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/email_templates/${template}`, {
    template_name,
    template_subject,
    template_body,
  })
}

export function removeEmailTemplate(template: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/email_templates/${template}/remove`, {})
}

/* -------------------------------------------------------------------------- */
/*  Sabloanele emailului catre furnizor ("Waiting for Third Party"): aceleasi  */
/*  capete si aceeasi tabela, cu `kind=third_party`.                           */
/* -------------------------------------------------------------------------- */

export interface ThirdPartyTemplatesShell extends EmailTemplatesShell {
  kind: 'third_party'
  /** false = baza n-are inca migrarea (`template_kind`). */
  ready: boolean
  /** Sablonul scris in cod, folosit cat timp nu e niciunul in baza. */
  builtin: { id: number; name: string; subject: string; body: string }
  /** false = baza n-are inca legaturile sablon-furnizor. */
  providersReady?: boolean
  /** Furnizorii externi, pentru "Used for providers". */
  providers?: Array<{ id: number; name: string }>
}

export function thirdPartyTemplatesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'email_templates', 'third_party'],
    queryFn: () => apiGet<ThirdPartyTemplatesShell>('/settings/easeedesk/email_templates?kind=third_party'),
  })
}

export function thirdPartyTemplatesListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'email_templates', 'third_party', 'list'],
    queryFn: () => apiGet<EmailTemplatesList>('/settings/easeedesk/email_templates/list?kind=third_party'),
  })
}

/** Raspunsul unei salvari care leaga furnizori: cei MUTATI de pe alt sablon (un furnizor = un sablon). */
export interface ProvidersSaved {
  ok?: boolean
  template?: { id: number; name: string }
  moved?: Array<{ provider: string; from: string }>
  message?: string
}

/** `providers` lipsa = legaturile nu se ating; lista goala = niciun furnizor. */
function providersField(providers?: number[]): Record<string, string> {
  return providers === undefined ? {} : { providers: providers.join(',') }
}

export function createThirdPartyTemplate(template_name: string, template_subject: string, template_body: string, providers?: number[]) {
  return apiPost<ProvidersSaved>('/settings/easeedesk/email_templates', {
    template_name,
    template_subject,
    template_body,
    template_kind: 'third_party',
    ...providersField(providers),
  })
}

export function updateThirdPartyTemplate(template: number, template_name: string, template_subject: string, template_body: string, providers?: number[]) {
  return apiPost<ProvidersSaved>(`/settings/easeedesk/email_templates/${template}`, {
    template_name,
    template_subject,
    template_body,
    ...providersField(providers),
  })
}

/**
 * "Save as template for <furnizor>", din formularul "Waiting for Third Party":
 * subiectul si corpul CU VARIABILE. Suprascrie sablonul furnizorului, sau face
 * unul nou "<furnizor> escalation" legat de el.
 */
export function saveTemplateForProvider(provider: number, template_subject: string, template_body: string) {
  return apiPost<{
    ok?: boolean
    created?: boolean
    template?: { id: number; name: string; subject: string; body: string; isDefault: boolean; builtin: boolean; providers: number[] }
  }>(`/settings/easeedesk/email_templates/provider/${provider}`, { template_subject, template_body })
}

export function setDefaultEmailTemplate(template: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/email_templates/${template}/default`, {})
}
